/**
 * Development Panel for Version Checker
 *
 * Provides UI buttons to manually test version-checker behaviour.
 * Only renders in development mode when explicitly enabled via localStorage.
 *
 * Enable:  localStorage.setItem('showVersionCheckerUI', 'true'); location.reload();
 * Disable: localStorage.removeItem('showVersionCheckerUI'); location.reload();
 */

import React from 'react';
import versionChecker, { AUTO_RELOAD_DELAY_MINUTES } from './version-checker';

const VersionCheckerDevPanel = () => {
  const [showPanel, setShowPanel] = React.useState(
    localStorage.getItem('showVersionCheckerUI') === 'true'
  );

  // Only render in development AND when the panel is explicitly enabled
  if (process.env.NODE_ENV !== 'development' || !showPanel) {
    return null;
  }

  const togglePanel = () => {
    const next = !showPanel;
    if (next) {
      localStorage.setItem('showVersionCheckerUI', 'true');
    } else {
      localStorage.removeItem('showVersionCheckerUI');
    }
    window.location.reload();
  };

  const testAlert = () => {
    window.dispatchEvent(
      new CustomEvent('showAlert', {
        detail: {
          type: 'important',
          title: `A new version of the application is available. The app will auto-reload in ${AUTO_RELOAD_DELAY_MINUTES} minutes. Click "Update Now" to reload immediately.`,
          confirmBtnText: 'UPDATE NOW',
          cancelBtnText: `Auto-Update in ${AUTO_RELOAD_DELAY_MINUTES} minutes`,
          showCloseBtn: true,
          size: 'lg',
          dialogClassName: 'version-update-alert',
          onConfirm: () => versionChecker.reloadApp(),
          onCancel: () => {},
        },
      })
    );
  };

  const checkVersion = async () => {
    try {
      const response = await fetch(`/meta.json?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const versionInfo = `Version: ${data.version || 'N/A'} | Build: ${
        data.buildTime ? new Date(data.buildTime).toLocaleString() : 'N/A'
      }`;

      window.dispatchEvent(
        new CustomEvent('showAlert', {
          detail: {
            type: 'success',
            title: versionInfo,
            confirmBtnText: 'OK',
            showCloseBtn: true,
          },
        })
      );
    } catch (error) {
      console.error('Failed to fetch version:', error);

      window.dispatchEvent(
        new CustomEvent('showAlert', {
          detail: {
            type: 'error',
            title: `Version check failed: ${error.message}. Check console for details.`,
            confirmBtnText: 'OK',
            showCloseBtn: true,
          },
        })
      );
    }
  };

  const manualCheck = () => {
    versionChecker.checkNow();
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      background: '#fff',
      border: '2px solid #007bff',
      borderRadius: '8px',
      padding: '15px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      minWidth: '250px',
    }}>
      <h6 style={{ margin: '0 0 10px 0', color: '#007bff', fontSize: '14px', fontWeight: 'bold' }}>
        Version Checker — Dev Panel
      </h6>
      <p style={{ margin: '0 0 5px 0', fontSize: '11px', color: '#666' }}>
        Development Mode Only
      </p>
      <p style={{ margin: '0 0 10px 0', fontSize: '11px', color: '#28a745', fontWeight: 'bold' }}>
        Version Checker: ✓ Running
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <button
          onClick={togglePanel}
          style={{
            padding: '8px 12px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          Hide Dev Panel
        </button>
        <button
          onClick={testAlert}
          style={{
            padding: '8px 12px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
          }}
        >
          Test Update Alert
        </button>
        <button
          onClick={checkVersion}
          style={{
            padding: '8px 12px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
          }}
        >
          Check Current Version
        </button>
        <button
          onClick={manualCheck}
          style={{
            padding: '8px 12px',
            background: '#ffc107',
            color: '#000',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
          }}
        >
          Manual Version Check
        </button>
      </div>
      <p style={{ margin: '10px 0 0 0', fontSize: '10px', color: '#999', fontStyle: 'italic' }}>
        This panel is hidden in production
      </p>
    </div>
  );
};

export default VersionCheckerDevPanel;
